import { Typography } from 'antd'
import { useContext } from 'react'
import VSpace from '../../../common/VSpace'
import { ItemsContext } from '../../../Items/ItemsRoot'
import { useLegacyJobDataQuery } from '../../../../domain/jobs/jobsQueries'
import useFeatureFlag from '../../../../hooks/useFeatureFlag'
import {
  isForTarget,
  useSetWorkareaAttachmentsMutation,
  useWorkareaAttachmentsQuery
} from '../../../../domain/workareas/groupsQueries'
import { WorkareaAttachmentDetail } from '../../../../domain/workareas/groupsApi'
import ProposalAttachments from '../../../Proposals/ProposalAttachments'
import { attachmentDisplayName, attachmentSubtitle, ProposalAttachment } from '../../../Proposals/proposalAttachmentsModel'

type Props = {
  /** Set for a grouped work area: the group is what the customer sees. */
  groupId?: string
  /** Set for an ungrouped work area, which carries its own attachments. */
  workareaId?: number
  /** Show the files without an uploader, for the group editor's read-only listing. */
  readOnly?: boolean
}

const toProposalAttachment = (x: WorkareaAttachmentDetail): ProposalAttachment => ({
  attachmentId: x.attachmentId,
  filename: x.filename,
  name: x.name,
  size: x.size,
  sortOrder: x.sortOrder
})

/**
 * Attachments shown beside the Proposal Description, so whoever writes the description that
 * the customer sees can attach the photos and files that go with it.
 *
 * These are held against the job rather than a proposal, and seed each new proposal's work
 * area attachments. A sent proposal keeps its own frozen copies.
 *
 * Gated on proposals-redesign like the rest of the feature. The Plan editor itself is not
 * flag-gated and is used by every tenant, so without this a tenant who never opted into the
 * redesign would still see the block appear.
 */
const ProposalDescriptionAttachments = ({ groupId, workareaId, readOnly = false }: Props) => {
  const redesignEnabled = useFeatureFlag('proposals-redesign')
  const { jobData: { jobId } } = useContext(ItemsContext)
  const jobQuery = useLegacyJobDataQuery(jobId)
  const attachmentsQuery = useWorkareaAttachmentsQuery(jobId)
  const saveMutation = useSetWorkareaAttachmentsMutation(jobId)

  const customerId = jobQuery.data?.customerId
  if (!redesignEnabled || customerId === undefined) {
    return null
  }

  const target = { groupId, workareaId }
  const rows = (attachmentsQuery.data ?? []).filter(x => isForTarget(x, target))
  const attachments = rows.map(toProposalAttachment)

  if (readOnly) {
    if (attachments.length === 0) {
      return <Typography.Text type="secondary">No attachments.</Typography.Text>
    }
    return <VSpace size="small">
      {attachments.map(attachment => <div key={attachment.attachmentId}>
        <div>{attachmentDisplayName(attachment)}</div>
        <Typography.Text type="secondary">{attachmentSubtitle(attachment)}</Typography.Text>
      </div>)}
    </VSpace>
  }

  const toRow = (x: ProposalAttachment): WorkareaAttachmentDetail => ({
    projectWorkareaId: groupId ? null : workareaId!,
    workareaGroupId: groupId ?? null,
    attachmentId: x.attachmentId,
    filename: x.filename,
    name: x.name,
    size: x.size,
    sortOrder: x.sortOrder ?? 0
  })

  return <ProposalAttachments
    attachments={attachments}
    customerId={customerId}
    jobId={jobId}
    onChange={next => saveMutation.replace(target, next.map(toRow))}
    onAdd={added => saveMutation.add(target, toRow(added))}
    embedded
  />
}

export default ProposalDescriptionAttachments
